import { datadogLogs, type LogsEvent } from '@datadog/browser-logs';
import type { ErrorInfo } from 'react';

import { version } from '../../package.json';
import type {
    Environment,
    PaymentAction,
    PaymentMethod,
    PaymentStatus
} from '../types';
import { PaymentError, type PaymentErrorCode } from './payment-error';
import type { PollingTimeoutContext } from './retry';

type ErrorCategory =
    | 'network'
    | 'validation'
    | 'psp'
    | 'unknown'
    | 'backend_timeout'
    | 'canceled'
    | 'failed';

const ERROR_CATEGORIES: Record<PaymentErrorCode, ErrorCategory> = {
    NETWORK_ERROR: 'network',
    VALIDATION_ERROR: 'validation',
    PSP_ERROR: 'psp',
    API_ERROR: 'unknown',
    GENERIC_ERROR: 'unknown',
    POLLING_TIMEOUT: 'backend_timeout',
    PAYMENT_CANCELED: 'canceled',
    PAYMENT_FAILED: 'failed',
    PAYMENT_INCOMPLETE: 'failed'
};

const SENSITIVE_QUERY_PARAMETER_PATTERN =
    /token|authorization|fingerprint|email|redirectResult|client[_-]?secret/i;
const SENSITIVE_TEXT_PARAMETER_PATTERN =
    /([?#&][^?&=\s]*(?:token|authorization|fingerprint|email|redirectResult|client[_-]?secret)[^?&=\s]*=)[^&\s"']+/gi;
const DATADOG_CLIENT_TOKEN = 'pubf3c1f97f9d6cdb9eae9b2ca241916c04';

const DATADOG_ENVIRONMENTS: Record<Environment, string> = {
    test: 'dev',
    sandbox: 'sandbox',
    live: 'prod'
};

const toDatadogEnvironment = (environment?: Environment): string | undefined =>
    environment ? DATADOG_ENVIRONMENTS[environment] : undefined;

function sanitizeText(value: string): string {
    return value.replace(SENSITIVE_TEXT_PARAMETER_PATTERN, '$1[REDACTED]');
}

function sanitizeUrl(value: string): string {
    try {
        const url = new URL(value, window.location.origin);
        url.searchParams.forEach((_, key) => {
            if (SENSITIVE_QUERY_PARAMETER_PATTERN.test(key)) {
                url.searchParams.set(key, '[REDACTED]');
            }
        });
        url.hash = sanitizeText(url.hash);
        return url.toString();
    } catch {
        return sanitizeText(value);
    }
}

function sanitizeErrorLike(target: { message?: string; stack?: string }): void {
    if (target.message) {
        target.message = sanitizeText(target.message);
    }
    if (target.stack) {
        target.stack = sanitizeText(target.stack);
    }
}

function sanitizeLogEvent(event: LogsEvent): boolean {
    event.message = sanitizeText(event.message);
    event.view.url = sanitizeUrl(event.view.url);

    if (event.view.referrer) {
        event.view.referrer = sanitizeUrl(event.view.referrer);
    }
    if (event.http?.url) {
        event.http.url = sanitizeUrl(event.http.url);
    }
    if (event.error) {
        sanitizeErrorLike(event.error);
        event.error.causes?.forEach(sanitizeErrorLike);
    }

    return true;
}

export interface WidgetTelemetryContext {
    environment: Environment;
    userSessionToken: string;
    paymentMethod?: PaymentMethod;
}

export interface MissingRedirectParamsContext {
    missingRedirectResult: boolean;
    missingPaymentToken: boolean;
    redirectResultInQuery: boolean;
    paymentTokenInQuery: boolean;
    hasHashRoute: boolean;
}

export type ExecuteBlockedReason =
    | 'REDIRECT_RESULT_MISSING'
    | 'EXECUTE_DATA_NOT_PROVIDED';

export interface MissingExecuteDataContext {
    reason: ExecuteBlockedReason;
    paymentStatus?: PaymentStatus;
    nextAction?: PaymentAction;
    hasActionPayload: boolean;
}

const MISSING_REDIRECT_PARAMS_MESSAGE =
    'Payment redirect parameters are missing';

export const toTelemetryContext = (
    { environment, userSessionToken, paymentMethod }: WidgetTelemetryContext,
    activePaymentMethod?: PaymentMethod
): WidgetTelemetryContext => ({
    environment,
    userSessionToken,
    paymentMethod: activePaymentMethod ?? paymentMethod
});

const paymentErrorAttribute = (
    details: Record<string, unknown>,
    widgetContext?: WidgetTelemetryContext
): Record<string, unknown> => ({
    paymentError: {
        ...details,
        paymentMethod: details.paymentMethod ?? widgetContext?.paymentMethod,
        environment: toDatadogEnvironment(widgetContext?.environment),
        widgetVersion: version,
        userSessionToken: widgetContext?.userSessionToken
    }
});

export const createLogsReporter = () => {
    let isInitialized = false;
    const reportedErrors = new WeakSet<PaymentError>();

    const initialize = (environment: Environment): void => {
        if (isInitialized) {
            return;
        }

        try {
            datadogLogs.init({
                clientToken: DATADOG_CLIENT_TOKEN,
                site: 'datadoghq.eu',
                service: 'spa-payment-widget',
                env: toDatadogEnvironment(environment),
                version,
                sessionPersistence: 'memory',
                sessionSampleRate: 100,
                forwardErrorsToLogs: false,
                forwardConsoleLogs: [],
                forwardReports: [],
                beforeSend: sanitizeLogEvent
            });
            isInitialized = true;
        } catch {
            // ignore
        }
    };

    /** Reports `error` at most once per `dedupeKey`, never throwing. */
    const report = (
        dedupeKey: PaymentError,
        error: Error,
        context: Record<string, unknown>
    ): void => {
        if (reportedErrors.has(dedupeKey)) {
            return;
        }

        try {
            datadogLogs.logger.error(error.message, context, error);
            reportedErrors.add(dedupeKey);
        } catch {
            // ignore
        }
    };

    const isObservableServerSide = (error: PaymentError): boolean =>
        Boolean(error.traceId);

    const reportPollingTimeout = (
        error: PaymentError,
        polling: PollingTimeoutContext,
        widgetContext?: WidgetTelemetryContext
    ): void =>
        report(
            error,
            error,
            paymentErrorAttribute(
                {
                    eventName: 'payment_error',
                    category: 'backend_timeout',
                    paymentErrorCode: error.code,
                    httpStatus: error.httpStatus,
                    paymentMethod: error.paymentMethod,
                    polling
                },
                widgetContext
            )
        );

    const reportPayment = (
        error: PaymentError,
        widgetContext?: WidgetTelemetryContext
    ): void => {
        if (isObservableServerSide(error)) {
            return;
        }

        report(
            error,
            error,
            paymentErrorAttribute(
                {
                    eventName: 'payment_error',
                    category: ERROR_CATEGORIES[error.code],
                    paymentErrorCode: error.code,
                    httpStatus: error.httpStatus,
                    paymentMethod: error.paymentMethod
                },
                widgetContext
            )
        );
    };

    const reportMissingExecuteData = (
        error: PaymentError,
        missingExecuteData: MissingExecuteDataContext,
        widgetContext?: WidgetTelemetryContext
    ): void =>
        report(
            error,
            error,
            paymentErrorAttribute(
                {
                    eventName: 'payment_error',
                    category: ERROR_CATEGORIES[error.code],
                    paymentErrorCode: error.code,
                    paymentMethod: error.paymentMethod,
                    missingExecuteData
                },
                widgetContext
            )
        );

    const reportRuntime = (
        error: Error,
        paymentError: PaymentError,
        errorInfo: ErrorInfo,
        widgetContext?: WidgetTelemetryContext
    ): void => {
        if (isObservableServerSide(paymentError)) {
            return;
        }

        report(
            paymentError,
            error,
            paymentErrorAttribute(
                {
                    eventName: 'runtime_error',
                    framework: 'react',
                    paymentErrorCode: paymentError.code,
                    paymentMethod: paymentError.paymentMethod,
                    componentStack: errorInfo.componentStack
                        ? sanitizeText(errorInfo.componentStack)
                        : undefined
                },
                widgetContext
            )
        );
    };

    const reportMissingRedirectParams = (
        redirect: MissingRedirectParamsContext,
        widgetContext?: WidgetTelemetryContext
    ): void => {
        try {
            datadogLogs.logger.error(
                MISSING_REDIRECT_PARAMS_MESSAGE,
                paymentErrorAttribute(
                    {
                        eventName: 'payment_redirect_parameters_missing',
                        redirect
                    },
                    widgetContext
                )
            );
        } catch {
            console.warn(
                'Failed to report missing payment redirect parameters'
            );
        }
    };

    return {
        initialize,
        reportMissingExecuteData,
        reportMissingRedirectParams,
        reportPollingTimeout,
        reportPayment,
        reportRuntime
    };
};

const logsReporter = createLogsReporter();

export const {
    initialize: initializeLogs,
    reportMissingExecuteData,
    reportMissingRedirectParams,
    reportPollingTimeout,
    reportPayment: reportPaymentError,
    reportRuntime: reportRuntimeError
} = logsReporter;
